import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ReactJson from "react-json-view";
import { ChangeMark, Collapsible, DiffList, RoleLabel } from "./CompareBits";
import {
  formatDelta,
  formatMoney,
  pickStep1Body,
  unwrapStageOutput,
} from "./evalOutput";
import { describeStep1Change } from "./step1Summary";

function CompareRow({ label, base, evalValue, changed }) {
  return (
    <tr className={changed ? "mse-cmprow mse-cmprow--changed" : undefined}>
      <td className="mse-cmprow__label">{label}</td>
      <td>{base ?? "—"}</td>
      <td>{evalValue ?? "—"}</td>
      <td className="mse-col-mark">
        <ChangeMark changed={Boolean(changed)} />
      </td>
    </tr>
  );
}

function formatIncome(income) {
  if (!income) {
    return "—";
  }
  if (!income.changed) {
    return formatMoney(income.candidate);
  }
  const percent =
    income.percentDelta == null ? "" : `, ${income.percentDelta}%`;

  return `${formatMoney(income.candidate)} (${formatDelta(income.delta)}${percent})`;
}

export default function Step1Diff({ caseRow, baseModel, evalModel }) {
  const structured = caseRow?.diff?.structured;
  const base = unwrapStageOutput(caseRow?.baselineOutputText);
  const candidate = unwrapStageOutput(caseRow?.candidate?.candidateOutputText);
  const baseBody = pickStep1Body(base.json);
  const evalBody = pickStep1Body(candidate.json);
  const income = structured?.qualifyingMonthlyIncome;
  const monetary = structured?.monetaryFigures;
  const toolCalls = structured?.toolCalls;
  const verdict = describeStep1Change(structured || {});

  const hasFigureDiff =
    monetary?.onlyInBaseline?.length > 0 || monetary?.onlyInCandidate?.length > 0;
  const hasToolDiff =
    toolCalls?.onlyInBaseline?.length > 0 ||
    toolCalls?.onlyInCandidate?.length > 0;

  return (
    <div className="mse-step1-diff">
      <div className={`mse-diffsummary mse-diffsummary--${verdict.tone}`}>
        <strong>{verdict.headline}</strong>
        <span className="mse-diffsummary__meta">{verdict.meta}</span>
      </div>

      <div className="mse-table-scroll">
        <table className="mse-table mse-table--compare">
          <thead>
            <tr>
              <th>Field</th>
              <th>
                <RoleLabel role="base" model={baseModel} />
              </th>
              <th>
                <RoleLabel role="eval" model={evalModel} />
              </th>
              <th className="mse-col-mark">Difference</th>
            </tr>
          </thead>
          <tbody>
            <CompareRow
              label="Status"
              base={structured?.status?.baseline}
              evalValue={structured?.status?.candidate}
              changed={structured?.status?.changed}
            />
            <CompareRow
              label="Qualifying monthly income"
              base={formatMoney(income?.baseline)}
              evalValue={formatIncome(income)}
              changed={income?.changed}
            />
            <CompareRow
              label="Reason"
              base={structured?.reason?.baseline}
              evalValue={structured?.reason?.candidate}
              changed={structured?.reason?.changed}
            />
            <CompareRow
              label="Status reason"
              base={structured?.statusReason?.baseline}
              evalValue={structured?.statusReason?.candidate}
              changed={structured?.statusReason?.changed}
            />
          </tbody>
        </table>
      </div>

      {hasFigureDiff && (
        <>
          <h4 className="mse-subhead">Dollar figures cited in the write-up</h4>
          <div className="mse-difflists">
            <DiffList
              title="Dropped by Eval"
              values={monetary?.onlyInBaseline}
              tone="dropped"
            />
            <DiffList
              title="Added by Eval"
              values={monetary?.onlyInCandidate}
              tone="added"
            />
          </div>
        </>
      )}

      {hasToolDiff && (
        <>
          <h4 className="mse-subhead">Income tools called</h4>
          <div className="mse-difflists">
            <DiffList
              title="Only Base called these"
              values={toolCalls?.onlyInBaseline}
              tone="dropped"
            />
            <DiffList
              title="Only Eval called these"
              values={toolCalls?.onlyInCandidate}
              tone="added"
            />
          </div>
        </>
      )}

      <Collapsible
        title="Full Step 1 write-up from each pipeline"
        meta={
          baseBody.field || evalBody.field
            ? `Base from .${baseBody.field || "—"} · Eval from .${evalBody.field || "—"}`
            : null
        }
      >
        <div className="mse-side-by-side mse-markdown-pair">
          <div>
            <h4>
              <RoleLabel role="base" model={baseModel} />
            </h4>
            <div className="mse-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {baseBody.text || "_Empty_"}
              </ReactMarkdown>
            </div>
          </div>
          <div>
            <h4>
              <RoleLabel role="eval" model={evalModel} />
            </h4>
            <div className="mse-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {evalBody.text || "_Empty_"}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </Collapsible>

      <Collapsible title="Raw Step 1 JSON">
        <div className="mse-side-by-side">
          <div>
            <h4>
              <RoleLabel role="base" model={baseModel} />
            </h4>
            {base.json ? (
              <ReactJson
                src={base.json}
                name={false}
                collapsed={2}
                displayDataTypes={false}
                enableClipboard={false}
              />
            ) : (
              <pre className="mse-pre">{base.text || "—"}</pre>
            )}
          </div>
          <div>
            <h4>
              <RoleLabel role="eval" model={evalModel} />
            </h4>
            {candidate.json ? (
              <ReactJson
                src={candidate.json}
                name={false}
                collapsed={2}
                displayDataTypes={false}
                enableClipboard={false}
              />
            ) : (
              <pre className="mse-pre">{candidate.text || "—"}</pre>
            )}
          </div>
        </div>
      </Collapsible>
    </div>
  );
}
